// © Microsoft Corporation. All rights reserved.

import { AppError, AppErrorPropertyBag, ErrorBehavior, Severity } from "@msnews/core-contracts";

/**
 * All app errors are set to create IcM ticket by default unless specified in individual error code otherwise.
 */
const severity = Severity.Alert;

/**
 * Build number is only passed in from updateResolverAppErrorsPropertyBag on page initialization
 */
const pb: AppErrorPropertyBag = {
    build: ""
};

/**
 * Append or update property bag properties. At page level intialization, this should be called to update the build number.
 * Since all app error objects has reference to this base property bag, calling this method will in fact update for all app errors.
 * @param newPbInfo Additional information needed for app error property bag
 */
export function updateResolverAppErrorsPropertyBag(newPbInfo: Partial<AppErrorPropertyBag>): void {
    Object.assign(pb, newPbInfo);
}

/**
 * Getter for the base app error property bag. Currently only used in unit test
 */
export function getResolverAppErrorsPropertyBag(): AppErrorPropertyBag {
    return pb;
}

const resolverSourceFile = "ConfigService.ts";
export const ResolverAppErrors = {
    /**
     * IndexedDb write failed in resolver.
     */
    IndexedDbWriteFailed: <AppError>{
        id: 22010,
        source: resolverSourceFile,
        severity,
        pb
    },

    /**
     * IndexedDb read failed in resolver.
     */
    IndexedDbReadFailed: <AppError>{
        id: 22011,
        source: resolverSourceFile,
        severity,
        pb
    },

    /**
     * Config Service Resolver fetch errors.
     */
    CrsFetchError: <AppError>{
        id: 22012,
        source: resolverSourceFile,
        severity,
        pb
    },

    /**
     * Config Service Resolver was enabled but we still went to Assets to getConfig and merge configs at client (classic resolver).
     */
    ReceivedConfigFromCdnAsCrsFetchFailed: <AppError>{
        id: 22013,
        source: resolverSourceFile,
        severity,
        pb
    },

    /**
     * Exception In GetConfig function.
     */
    GetConfigException: <AppError>{
        id: 22014,
        source: resolverSourceFile,
        severity: Severity.Critical,
        pb
    },

    /**
     * IndexedDb bulk read failed in resolver.
     */
    IndexedDbBulkReadFailed: <AppError>{
        id: 22015,
        source: resolverSourceFile,
        severity,
        pb
    },

    /**
     * IndexedDb failed to remove cached config
     */
    IndexedDbFailedToRemoveCachedConfig: <AppError>{
        id: 22016,
        source: resolverSourceFile,
        severity,
        pb
    },

    /**
     * IndexedDb failed to clear all configs
     */
    IndexedDbFailedToClearAllCachedConfigs: <AppError>{
        id: 22017,
        source: resolverSourceFile,
        severity,
        pb
    },

    /**
     * Render cache is purged due to changes in config
     */
    RenderCachePurgedDueToChangesInConfig: <AppError>{
        id: 22018,
        source: resolverSourceFile,
        severity: Severity.NoAlert,
        pb
    },

    /**
     * Render cache failed purge even though config changes were detected.
     */
    FailedToPurgeRenderCache: <AppError>{
        id: 22019,
        source: resolverSourceFile,
        severity,
        pb
    },

    /**
     * Failed to refresh config cache
     */
    RefreshConfigCacheFailed: <AppError>{
        id: 22020,
        source: resolverSourceFile,
        severity,
        pb
    },

    /**
     * Failed to initialize indexedDb
     */
    IndexedDbInitFailed: <AppError>{
        id: 22021,
        source: resolverSourceFile,
        severity,
        pb
    },

    /**
     * Different cache busting identifier
     */
    DifferentCbid: <AppError>{
        id: 22022,
        source: resolverSourceFile,
        severity: Severity.NoAlert,
        pb
    },

    /**
     * Error writing prg flights to localStorage
     */
    PrgFlightLsWriteError: <AppError>{
        id: 22023,
        source: resolverSourceFile,
        severity: Severity.NoAlert,
        pb
    },

    /**
     * Error reading prg flights to localStorage
     */
    PrgFlightLsReadError: <AppError>{
        id: 22024,
        source: resolverSourceFile,
        severity: Severity.NoAlert,
        pb
    }
};

/**
 * Configs served from CDN after CRS fetch failed
 */
export const ResolverErrorConfigsDownloadedByCdn: AppError = {
    id: 22025,
    source: resolverSourceFile,
    severity: Severity.NoAlert,
    pb
};

/**
 * Cached configs are for different flight
 */
export const CachedConfigsForDifferentFlight: AppError = {
    id: 22026,
    source: resolverSourceFile,
    severity: Severity.NoAlert,
    pb
};

/**
 * CRS sent non-200 response
 */
export const ConfigCrsFetchNon200Response: AppError = {
    id: 22027,
    source: resolverSourceFile,
    severity: Severity.Critical,
    pb
};

/**
 * Cdn config fetch received non-200 response
 */
export const ConfigCdnFetchNon200Response: AppError = {
    id: 22028,
    source: resolverSourceFile,
    severity,
    pb
};

/**
 * Invalid config ref received to resolve configs when Git configs is enabled
 */
export const GitConfigsInvalidConfigRef: AppError = {
    id: 22029,
    source: resolverSourceFile,
    severity: Severity.Critical,
    pb
};

/**
 * Invalid configResolver object for Git configs flight
 */
export const GitConfigsInvalidConfigResolver: AppError = {
    id: 22030,
    source: resolverSourceFile,
    severity: Severity.Critical,
    pb
};

/**
 * Config Service Resolver timeout.
 */
export const CrsFetchTimeout: AppError = {
    id: 22031,
    source: resolverSourceFile,
    severity,
    pb
};

/** Starts from 22050 */
export const FallbackResolverAppErrors = {
    /**
     * getConfigRecursive function try catch failed.
     */
    RecursiveGetFailed: <AppError>{
        id: 22050,
        source: resolverSourceFile,
        severity,
        pb
    },

    /**
     * BlobFetcher fetch failed for configs or config index.
     */
    BlobFetchReturnedNull: <AppError>{
        id: 22051,
        source: resolverSourceFile,
        severity: Severity.NoAlert,
        pb
    }
};

/**
 * Error that when Server Cache config refresh failed
 */
export const ServerCacheRefreshFailed: AppError = {
    id: 34009,
    source: "SsrResolver.ts",
    severity: Severity.NoAlert,
    pb
};

/**
 * Error that when SSR failed to getConfig.
 */
export const ServerCacheFetchFailed: AppError = {
    id: 34005,
    source: "SsrResolver.ts",
    severity: Severity.NoAlert,
    pb
};

/**
 * SSR config resolver returned null config
 */
export const SsrResolverReturnedNullConfig: AppError = {
    id: 34011,
    source: "SsrResolver.ts",
    severity: Severity.HighImpact,
    pb
};